/**
 * SchedulePage
 *
 * Renders the full clinic schedule view for a given clinic route parameter
 * (/report/:clinicId/schedule).  It supports three calendar modes (Day, Week,
 * Month) and two perspectives (Practice — whole clinic — and Employee — per
 * practitioner).
 *
 * API endpoints / hooks used:
 *  - usePractitioners({ clinic })          → GET /practitioners/?clinic=<id>
 *  - useClinicVacations({ clinic })        → GET /clinic-vacations/?clinic=<id>
 *  - usePractitionerVacations({ clinic })  → GET /practitioner-vacations/?clinic=<id>
 *  - usePractitionerWeeklySchedules({ clinic })
 *                                          → GET /practitioner-weekly-schedules/?clinic=<id>
 *  - useClinics({ limit, offset })         → GET /clinics/ (org-wide roles only)
 *
 * Scheduling / vacation data handled:
 *  - practitioner_weekly_schedule rows     — recurring Mon-Sun shift template, plus
 *    date-bounded rows that override it inside their effective range (resolved by
 *    slotForDate from @utils/weeklySchedule)
 *  - PractitionerVacation.date_ranges      — leave blocks per practitioner. `status` and
 *    `leave_type` are embedded per RANGE, not on the row; only ranges with
 *    status "approved" (or null, for legacy rows) are treated as leave.
 *  - PractitionerVacation.date_ranges[].leave_type — "vacation" | "sick" | "personal" | "unpaid"
 *  - ClinicVacation.date_ranges            — whole-clinic closure blocks
 */
import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  usePractitioners,
  useClinicVacations,
  usePractitionerVacations,
  usePractitionerScheduleOverrides,
  useCreatePractitionerScheduleOverride,
  useUpdatePractitionerScheduleOverride,
  usePractitionerWeeklySchedules,
  useClinics,
  useClinicDetail,
} from "@api/hooks";
import { getHolidayMap } from "@utils/canadianHolidays";
import {
  groupSchedulesByPractitioner,
  slotBreaks,
  slotForDate,
  workingMinutes,
} from "@utils/weeklySchedule";
import { useAuth } from "@api/useAuth";
import { getRoster, getInitials } from "@utils/practitionerUtils"; // getInitials moved from local to shared
import { normalizeTypeForUi, safeText, pickBadgeColor } from "@utils/utils";
import { TYPE_LABELS, TYPE_ORDER, typeColor } from "@utils/practitionerTypes"; // replaces SCHED_TYPE_LABELS/COLORS/BADGE_COLORS/ORDER local maps
import { canAccessAllClinics, isManagerOrAbove } from "@utils";
import { Badge, Button, EditShiftModal, showSuccess } from "@common";

// ── Helpers ────────────────────────────────────────────────────────────────────

const DAY_KEYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

// Returns the effective time slot for a practitioner on a given date, applying
// override priority: practitioner override > clinic closure > weekly schedule.
// The weekly-schedule layer resolves practitioner_weekly_schedule rows, so a
// date-bounded row wins over the practitioner's base row inside its range.
// Returns { start, end } or null (not working / override is a day-off / clinic closed).
//
// This mirrors backend `resolve_availability` (apps/practitioners/availability.py). The
// clinic-closure layer was missing, so an Employee week/month cell rendered "9:00 AM –
// 5:00 PM" on a date the Practice month view (which does consult closures) showed as
// "Closed". Callers that omit `clinicClosures` keep the old two-layer behaviour.
function getEffectiveSlot(
  p,
  dateStr,
  dayKey,
  overridesByPractitioner,
  schedulesByPractitioner,
  clinicClosures = null,
) {
  for (const ov of overridesByPractitioner?.[p.id] ?? []) {
    if (isInRanges(dateStr, ov.date_ranges)) {
      if (!ov.is_open) return null;
      const start = ov.start_time || null;
      const end = ov.end_time || null;
      return start && end ? { start, end } : null;
    }
  }
  // A practitioner override outranks a closure (matching the backend chain), so this
  // check sits below the loop above and above the weekly template.
  if (clinicClosures?.some((cv) => isInRanges(dateStr, cv.date_ranges))) return null;
  return slotForDate(schedulesByPractitioner?.[p.id], dateStr, dayKey);
}

// Interactive props that turn a calendar cell into an "open the day editor" target.
// Returns {} when the viewer cannot manage shifts (onShiftClick is null), so a read-only
// calendar keeps its original non-interactive markup.
//
// `slot` may be null: an empty ("Off") cell is editable too, which is what makes a deleted
// occurrence recoverable. Without it, "Delete shift" removed the chip and left no way back
// — the day became uneditable from the calendar. A null slot also covers adding a one-off
// shift on a day the recurring template leaves off.
//
// Callers must NOT offer this on leave days or clinic closures — see shouldOfferShiftEdit.
function shiftChipProps(onShiftClick, practitioner, dateStr, slot) {
  if (!onShiftClick || !practitioner || !dateStr) return {};
  const open = () => onShiftClick(practitioner, dateStr, slot ?? null);
  return {
    role: "button",
    tabIndex: 0,
    onClick: open,
    onKeyDown: (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        open();
      }
    },
    title: slot?.start ? "Edit this shift" : "Add a shift for this day",
  };
}

// Cursor/hover affordance for a clickable cell. Kept separate from shiftChipProps so
// spreading those props can never clobber a site's own className.
function shiftChipClass(onShiftClick) {
  return onShiftClick
    ? " cursor-pointer hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-palette-blue-ring"
    : "";
}

// Whether an empty cell should offer "add a shift". Leave days belong to the Time Off flow,
// and a clinic closure means the practice is shut — neither should be overridden by
// accident from a calendar cell.
function shouldOfferShiftEdit(onShiftClick, { onLeave = false, clinicClosed = false } = {}) {
  return onShiftClick && !onLeave && !clinicClosed ? onShiftClick : null;
}

// The one-day override row covering `dateStr`, or null. Lets the editor PATCH an existing
// override instead of stacking a second row for the same date.
function findOverrideForDate(overridesByPractitioner, practitionerId, dateStr) {
  return (
    (overridesByPractitioner?.[practitionerId] ?? []).find((ov) =>
      isInRanges(dateStr, ov.date_ranges),
    ) ?? null
  );
}

function toDateStr(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function isInRanges(dateStr, ranges = []) {
  return (ranges ?? []).some((r) => r.start_date <= dateStr && dateStr <= r.end_date);
}

// Returns the leave_type string ("vacation" | "sick" | "personal" | "unpaid") rather than a
// boolean so calendar cells can render the appropriate V/S/P/U indicator instead of just
// a generic "off" state.
function getLeaveForDate(dateStr, ranges = []) {
  for (const r of ranges ?? []) {
    if (r.start_date <= dateStr && dateStr <= r.end_date) return r.leave_type || "vacation";
  }
  return null;
}

function formatFullDate(date) {
  return date.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

// Parse "9:00 AM" → minutes since midnight for duration calculation
function parseTimeToMinutes(str) {
  if (!str) return 0;
  const m = str.match(/(\d+):(\d+)\s*(AM|PM)/i);
  if (!m) return 0;
  let h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  const period = m[3].toUpperCase();
  if (period === "PM" && h !== 12) h += 12;
  if (period === "AM" && h === 12) h = 0;
  return h * 60 + min;
}

// ── Week helpers ───────────────────────────────────────────────────────────────

function getWeekStart(date) {
  const d = new Date(date);
  d.setDate(d.getDate() - d.getDay()); // rewind to Sunday
  d.setHours(0, 0, 0, 0);
  return d;
}

function formatWeekLabel(date) {
  const start = getWeekStart(date);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  const fmt = (d) => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return `${fmt(start)} – ${fmt(end)}`;
}

function buildWeekDays(date, todayStr) {
  const start = getWeekStart(date);
  return DAY_KEYS.map((dayKey, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    const dStr = toDateStr(d);
    return {
      dayKey,
      dayShort: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][i],
      dayNum: d.getDate(),
      dateStr: dStr,
      isToday: dStr === todayStr,
    };
  });
}

function getMonthLabel(date) {
  return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

function buildMonthDays(date, todayStr) {
  const year = date.getFullYear();
  const month = date.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayOfWeek = new Date(year, month, 1).getDay(); // 0 = Sunday

  const days = [];
  // Leading empty cells so day 1 lands on the correct weekday column
  for (let i = 0; i < firstDayOfWeek; i++) days.push(null);
  // All days of the month
  for (let d = 1; d <= daysInMonth; d++) {
    const dateObj = new Date(year, month, d);
    const dStr = toDateStr(dateObj);
    days.push({
      dayNum: d,
      dayKey: DAY_KEYS[dateObj.getDay()],
      dateStr: dStr,
      isToday: dStr === todayStr,
    });
  }
  // Trailing empty cells to complete the last row
  const remainder = days.length % 7;
  if (remainder !== 0) {
    for (let i = 0; i < 7 - remainder; i++) days.push(null);
  }
  return days;
}

// Unlike buildMonthDays (which pads with null sentinels), this version fills every grid cell
// with a real date object from the previous or next month, tagged with isCurrentMonth: false,
// so the Practice month view can still compute dayCountsMap for those overflow cells.
function buildPracticeMonthGrid(date, todayStr) {
  const year = date.getFullYear();
  const month = date.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayOfWeek = new Date(year, month, 1).getDay();
  const prevMonthLastDay = new Date(year, month, 0).getDate();

  const days = [];

  // Leading days from previous month — fills the partial first row before day 1.
  for (let i = firstDayOfWeek - 1; i >= 0; i--) {
    const d = new Date(year, month - 1, prevMonthLastDay - i);
    const dStr = toDateStr(d);
    days.push({
      dayNum: d.getDate(),
      dayKey: DAY_KEYS[d.getDay()],
      dateStr: dStr,
      isToday: dStr === todayStr,
      isCurrentMonth: false,
    });
  }

  // Current month days
  for (let d = 1; d <= daysInMonth; d++) {
    const dateObj = new Date(year, month, d);
    const dStr = toDateStr(dateObj);
    days.push({
      dayNum: d,
      dayKey: DAY_KEYS[dateObj.getDay()],
      dateStr: dStr,
      isToday: dStr === todayStr,
      isCurrentMonth: true,
    });
  }

  // Trailing days from next month to complete the last row
  const remainder = days.length % 7;
  if (remainder !== 0) {
    for (let i = 1; i <= 7 - remainder; i++) {
      const d = new Date(year, month + 1, i);
      const dStr = toDateStr(d);
      days.push({
        dayNum: d.getDate(),
        dayKey: DAY_KEYS[d.getDay()],
        dateStr: dStr,
        isToday: dStr === todayStr,
        isCurrentMonth: false,
      });
    }
  }

  return days;
}

// ── Sub-components ─────────────────────────────────────────────────────────────

// Leave type → letter + color for the indicator circle
const LEAVE_INDICATORS = {
  vacation: { letter: "V", bg: "bg-palette-amber-badge", text: "text-white", title: "Vacation" },
  sick: { letter: "S", bg: "bg-palette-red-label", text: "text-white", title: "Sick leave" },
  personal: {
    letter: "P",
    bg: "bg-palette-violet-badge",
    text: "text-white",
    title: "Personal day",
  },
  unpaid: { letter: "U", bg: "bg-palette-gray-badge", text: "text-white", title: "Unpaid leave" },
};

function LeaveIndicator({ leaveType }) {
  const ind = LEAVE_INDICATORS[leaveType] ?? LEAVE_INDICATORS.vacation;
  return (
    <span
      title={ind.title}
      className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold shrink-0 ${ind.bg} ${ind.text}`}
    >
      {ind.letter}
    </span>
  );
}

// Displays a compact holiday label inside a calendar cell.
// Red background = paid statutory holiday; gray = observed/non-paid.
function HolidayChip({ holiday }) {
  if (!holiday) return null;
  const label = holiday.isPaidStat ? `${holiday.name} · Paid Stat` : `${holiday.name} · Observed`;
  return (
    <span
      title={label}
      className={`block truncate text-xs font-semibold px-1.5 py-0.5 rounded leading-snug ${
        holiday.isPaidStat
          ? "bg-palette-red-muted text-palette-red-heading"
          : "bg-palette-gray-subtle text-palette-gray-secondary"
      }`}
    >
      {holiday.name}
    </span>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="bg-white rounded-2xl border border-palette-gray-subtle p-5 shadow-sm">
      <p className="text-xs font-semibold text-palette-gray-placeholder uppercase tracking-widest mb-2">
        {label}
      </p>
      <p className="text-4xl font-bold text-palette-slate-text">{value}</p>
    </div>
  );
}

// ── Day detail panel (Practice month view sidebar) ────────────────────────────

// ── Practice month view ────────────────────────────────────────────────────────

const DAY_HEADERS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function PracticeMonthView({
  roster,
  practiceMonthGrid,
  clinicClosures,
  vacationsByProfile,
  overridesByPractitioner,
  schedulesByPractitioner,
  holidayMap, // keyed by YYYY-MM-DD; values: { name, isPaidStat }
  onShiftClick, // forwarded to the day-detail timeline; null when the viewer cannot edit
}) {
  const [selectedDay, setSelectedDay] = useState(null);

  // ── Modal data — computed only when a day is selected ────────────────────
  const modalStaffForDay = useMemo(() => {
    if (!selectedDay) return [];
    return roster
      .map((p) => {
        const slot = getEffectiveSlot(
          p,
          selectedDay.dateStr,
          selectedDay.dayKey,
          overridesByPractitioner,
          schedulesByPractitioner,
        );
        const hasSlot = !!(slot?.start && slot?.end);
        const pid = p.id;
        const onVacation = isInRanges(selectedDay.dateStr, vacationsByProfile[pid]);
        return { p, slot: hasSlot ? slot : null, onVacation, working: hasSlot && !onVacation };
      })
      .filter(({ slot, onVacation }) => slot || onVacation);
  }, [selectedDay, roster, vacationsByProfile, overridesByPractitioner, schedulesByPractitioner]);

  const modalClosed = selectedDay
    ? clinicClosures.some((cv) => isInRanges(selectedDay.dateStr, cv.date_ranges))
    : false;

  const modalOffToday = useMemo(() => {
    if (!selectedDay) return [];
    return roster.filter((p) => {
      const pid = p.id;
      return isInRanges(selectedDay.dateStr, vacationsByProfile[pid]);
    });
  }, [selectedDay, roster, vacationsByProfile]);

  const modalDateLabel = selectedDay
    ? new Date(selectedDay.dateStr + "T00:00:00").toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      })
    : "";
  const modalDayName = selectedDay
    ? new Date(selectedDay.dateStr + "T00:00:00")
        .toLocaleDateString("en-US", { weekday: "long" })
        .toUpperCase()
    : "";
  const modalWorking = modalStaffForDay.filter((x) => x.working).length;
  const modalTotalHours = Math.round(
    modalStaffForDay.reduce((acc, { slot, working }) => {
      if (!working || !slot) return acc;
      const diff = workingMinutes(slot); // net of breaks
      return diff > 0 ? acc + diff / 60 : acc;
    }, 0),
  );

  // Pre-compute working counts per date (excludes staff on approved/legacy vacation)
  const dayCountsMap = useMemo(() => {
    const map = {};
    for (const day of practiceMonthGrid) {
      const dds = roster.filter((p) => {
        const s = slotForDate(schedulesByPractitioner?.[p.id], day.dateStr, day.dayKey);
        if (!(s?.start && s?.end)) return false;
        if (normalizeTypeForUi(p?.practitioner_type) !== "DDS") return false;
        const pid = p.id;
        return !isInRanges(day.dateStr, vacationsByProfile[pid]);
      }).length;
      const hyg = roster.filter((p) => {
        const s = slotForDate(schedulesByPractitioner?.[p.id], day.dateStr, day.dayKey);
        if (!(s?.start && s?.end)) return false;
        if (normalizeTypeForUi(p?.practitioner_type) !== "HYG") return false;
        const pid = p.id;
        return !isInRanges(day.dateStr, vacationsByProfile[pid]);
      }).length;
      map[day.dateStr] = { dds, hyg };
    }
    return map;
  }, [practiceMonthGrid, roster, vacationsByProfile, schedulesByPractitioner]);

  function isClosed(dateStr) {
    return clinicClosures.some((cv) => isInRanges(dateStr, cv.date_ranges));
  }

  return (
    <div>
      {/* ── Calendar — always full width ───────────────────────────────────── */}
      <div>
        <div className="bg-white rounded-2xl border border-palette-gray-subtle shadow-sm overflow-hidden mb-4">
          {/* Column headers */}
          <div className="grid grid-cols-7 border-b border-palette-gray-subtle">
            {DAY_HEADERS.map((d) => (
              <div
                key={d}
                className="py-3 text-center text-xs font-semibold text-palette-gray-placeholder uppercase tracking-wide border-r border-palette-gray-subtle last:border-r-0"
              >
                {d}
              </div>
            ))}
          </div>

          {/* Day cells */}
          <div className="grid grid-cols-7">
            {practiceMonthGrid.map((day, i) => {
              const closed = isClosed(day.dateStr);
              const counts = dayCountsMap[day.dateStr] ?? { dds: 0, hyg: 0 };
              const dim = !day.isCurrentMonth;
              const isLastCol = (i + 1) % 7 === 0;
              const isSelected = selectedDay?.dateStr === day.dateStr;

              return (
                <div
                  key={day.dateStr}
                  onClick={() =>
                    setSelectedDay((prev) => (prev?.dateStr === day.dateStr ? null : day))
                  }
                  className={`min-h-[130px] p-3 flex flex-col gap-1.5 border-b border-r border-palette-gray-subtle cursor-pointer transition-colors ${
                    isLastCol ? "border-r-0" : ""
                  } ${
                    isSelected
                      ? "bg-palette-blue-soft/50 ring-2 ring-inset ring-palette-indigo-ring"
                      : dim
                        ? "bg-palette-gray-soft/40 hover:bg-palette-gray-soft/70"
                        : "hover:bg-palette-gray-soft/60"
                  }`}
                >
                  {/* Date number */}
                  <div className="mb-0.5">
                    <span
                      className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-sm font-bold ${
                        day.isToday
                          ? "bg-palette-blue-badge text-white"
                          : dim
                            ? "text-palette-gray-border"
                            : "text-palette-slate-heading"
                      }`}
                    >
                      {day.dayNum}
                    </span>
                  </div>

                  {/* Statutory holiday chip — only for non-dim cells to avoid visual noise */}
                  {!dim && <HolidayChip holiday={holidayMap[day.dateStr]} />}

                  {/* Content */}
                  {closed ? (
                    <span
                      className={`text-xs ${dim ? "text-palette-gray-border" : "text-palette-gray-placeholder"}`}
                    >
                      Closed
                    </span>
                  ) : (
                    <>
                      {counts.dds > 0 && (
                        <span
                          className={`px-2 py-1 rounded-md text-xs font-medium ${
                            dim
                              ? "bg-palette-blue-soft text-palette-blue-ring"
                              : "bg-palette-blue-muted text-palette-blue-heading"
                          }`}
                        >
                          Dentist · {counts.dds}
                        </span>
                      )}
                      {counts.hyg > 0 && (
                        <span
                          className={`px-2 py-1 rounded-md text-xs font-medium ${
                            dim
                              ? "bg-palette-emerald-soft text-palette-emerald-ring"
                              : "bg-palette-emerald-muted text-palette-emerald-hover"
                          }`}
                        >
                          Hygienist · {counts.hyg}
                        </span>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Roles legend ────────────────────────────────────────────── */}
        <RolesLegend />
      </div>
      {/* end calendar */}

      {/* ── Day modal overlay ─────────────────────────────────────────────── */}
      {selectedDay && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/25 backdrop-blur-sm"
          onClick={() => setSelectedDay(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[88vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-palette-gray-subtle shrink-0">
              <div>
                <p className="text-xs font-semibold text-palette-gray-placeholder uppercase tracking-widest mb-0.5">
                  {modalDayName}
                </p>
                <p className="text-xl font-bold text-palette-slate-text">{modalDateLabel}</p>
                {/* Show statutory holiday name in the modal header when applicable */}
                {selectedDay && holidayMap[selectedDay.dateStr] && (
                  <span
                    className={`mt-1 inline-block text-xs font-semibold px-2 py-0.5 rounded ${
                      holidayMap[selectedDay.dateStr].isPaidStat
                        ? "bg-palette-red-muted text-palette-red-heading"
                        : "bg-palette-gray-subtle text-palette-gray-secondary"
                    }`}
                  >
                    {holidayMap[selectedDay.dateStr].name}
                    {holidayMap[selectedDay.dateStr].isPaidStat && " · Paid Stat"}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                {!modalClosed && (
                  <>
                    <span className="text-xs font-semibold text-palette-blue-badge bg-palette-blue-soft px-3 py-1.5 rounded-full">
                      {modalWorking} working
                    </span>
                    <span className="text-xs font-semibold text-palette-slate-secondary bg-palette-gray-subtle px-3 py-1.5 rounded-full">
                      {modalTotalHours}h total
                    </span>
                  </>
                )}
                <button
                  type="button"
                  onClick={() => setSelectedDay(null)}
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-palette-gray-subtle text-palette-gray-placeholder hover:text-palette-gray-badge text-xl transition-colors"
                >
                  ×
                </button>
              </div>
            </div>

            {/* Modal body — timeline */}
            <div className="flex-1 overflow-y-auto p-4">
              <PracticeDayTimeline
                staffForDay={modalStaffForDay}
                clinicClosed={modalClosed}
                practLoading={false}
                dateStr={selectedDay?.dateStr}
                onShiftClick={onShiftClick}
              />
            </div>

            {/* Modal footer — off today */}
            {modalOffToday.length > 0 && (
              <div className="px-6 py-3 border-t border-palette-gray-subtle shrink-0 flex items-center gap-2">
                <span className="text-palette-gray-placeholder">🌙</span>
                <p className="text-xs text-palette-gray-secondary">
                  <span className="font-semibold text-palette-gray-badge">Off today: </span>
                  {modalOffToday.map((p) => safeText(p?.name)).join(", ")}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Practice Day Timeline ──────────────────────────────────────────────────────

const TIMELINE_START_MIN = 7 * 60; // 7:00 AM — start where most shifts begin
const TIMELINE_END_MIN = 21 * 60; // 9:00 PM
const TIMELINE_RANGE = TIMELINE_END_MIN - TIMELINE_START_MIN; // 840 min
const PX_PER_MIN = 2; // 2 px/min = 120 px/hr — matches reference density
const TIMELINE_TOTAL_PX = TIMELINE_RANGE * PX_PER_MIN; // 1680 px total

// Break bands drawn over a shift block. 44px inside the block's 48px height leaves a sliver
// of the role colour above and below, so the break reads as carved out of the shift rather
// than as a separate block. The diagonal hatch distinguishes "not working" from the solid
// role colours without introducing another hue.
const BREAK_BAND_HEIGHT_PX = 44;
const BREAK_BAND_HATCH =
  "repeating-linear-gradient(135deg, oklch(0.93 0.008 250) 0px, oklch(0.93 0.008 250) 6px," +
  " oklch(0.87 0.012 250) 6px, oklch(0.87 0.012 250) 12px)";
// Below this width the "Break" label clips to a sliver, so the band shows the hatch alone.
const BREAK_LABEL_MIN_PX = 48;
const LEFT_COL_PX = 220;

// One label per 30-minute slot — matching the reference header density
const TIME_MARKERS = (() => {
  const marks = [];
  for (let m = TIMELINE_START_MIN; m <= TIMELINE_END_MIN; m += 30) {
    const h = Math.floor(m / 60);
    const min = m % 60;
    const label =
      min === 0
        ? `${h % 12 === 0 ? 12 : h % 12}:00 ${h < 12 ? "AM" : "PM"}`
        : `${h % 12 === 0 ? 12 : h % 12}:30`;
    marks.push({ label, left: (m - TIMELINE_START_MIN) * PX_PER_MIN, isHour: min === 0 });
  }
  return marks;
})();

function tlLeft(timeStr) {
  const min = parseTimeToMinutes(timeStr);
  return Math.max(0, Math.min(min - TIMELINE_START_MIN, TIMELINE_RANGE)) * PX_PER_MIN;
}

function tlWidth(startStr, endStr) {
  const s = parseTimeToMinutes(startStr);
  const e = parseTimeToMinutes(endStr);
  // handle midnight-crossing shifts by clamping to timeline end
  const dur = e > s ? e - s : TIMELINE_RANGE - (s - TIMELINE_START_MIN);
  return Math.max(40, Math.min(dur, TIMELINE_RANGE)) * PX_PER_MIN;
}

/**
 * Left/width in px for an arbitrary band, clipped to the visible 7 AM–9 PM timeline.
 * Returns null when the band falls entirely outside it.
 *
 * Not tlWidth: that floors a shift block at 40 minutes so short shifts stay readable, which
 * would draw a 15-minute break nearly three times too wide. It also only clamps the left
 * edge, so a break starting before 7 AM would be drawn at the origin at full width — here
 * both ends are clipped instead. A band whose end is not after its start (an overnight
 * break) is dropped rather than guessed at.
 */
function tlBand(startStr, endStr) {
  const s = parseTimeToMinutes(startStr);
  const e = parseTimeToMinutes(endStr);
  if (s === null || e === null || e <= s) return null;
  const from = Math.max(s, TIMELINE_START_MIN);
  const to = Math.min(e, TIMELINE_END_MIN);
  if (to <= from) return null;
  return {
    left: (from - TIMELINE_START_MIN) * PX_PER_MIN,
    width: (to - from) * PX_PER_MIN,
  };
}

function PracticeDayTimeline({
  staffForDay,
  clinicClosed,
  practLoading,
  dateStr, // the date this timeline represents — needed to scope a shift edit
  onShiftClick, // null when the viewer cannot manage shifts
}) {
  if (practLoading) {
    return (
      <div className="bg-white rounded-2xl border border-palette-gray-subtle shadow-sm py-20 text-center text-sm text-palette-gray-placeholder">
        Loading schedule…
      </div>
    );
  }
  if (clinicClosed) {
    return (
      <div className="bg-white rounded-2xl border border-palette-gray-subtle shadow-sm py-20 text-center">
        <div className="text-4xl mb-3">🔒</div>
        <p className="text-base font-semibold text-palette-slate-secondary">Clinic Closed</p>
      </div>
    );
  }
  if (staffForDay.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-palette-gray-subtle shadow-sm py-20 text-center">
        <p className="text-sm text-palette-gray-placeholder">No staff scheduled for this day.</p>
      </div>
    );
  }

  const workingCount = staffForDay.filter((x) => x.working).length;

  return (
    <div className="bg-white rounded-2xl border border-palette-gray-subtle shadow-sm overflow-hidden">
      <div className="flex">
        {/* ── Fixed left staff column ───────────────────────────────────── */}
        <div
          style={{ width: LEFT_COL_PX, minWidth: LEFT_COL_PX }}
          className="shrink-0 border-r border-palette-gray-subtle bg-white z-10"
        >
          {/* Column header */}
          <div className="h-10 flex items-center px-4 border-b border-palette-gray-subtle">
            <span className="text-xs font-semibold text-palette-gray-placeholder uppercase tracking-wide">
              All Members · {workingCount}
            </span>
          </div>
          {/* Staff rows */}
          {staffForDay.map(({ p, onVacation, leaveType }) => {
            const name = safeText(p?.name) || "—";
            const type = normalizeTypeForUi(p?.practitioner_type);
            const leaveLabel =
              {
                vacation: "Vacation",
                sick: "Sick Leave",
                personal: "Personal Day",
                unpaid: "Unpaid Leave",
              }[leaveType] ?? "Time Off";
            const leaveColor =
              {
                vacation: "text-palette-amber-badge",
                sick: "text-palette-red-label",
                personal: "text-palette-violet-badge",
                unpaid: "text-palette-gray-badge",
              }[leaveType] ?? "text-palette-amber-label";
            return (
              <div
                key={p.id}
                className="h-16 flex items-center gap-3 px-4 border-b border-palette-gray-subtle last:border-b-0"
              >
                <div
                  className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${typeColor(type)}`}
                >
                  {getInitials(name)}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-palette-slate-text leading-snug truncate">
                    {name}
                  </p>
                  <p className="text-xs text-palette-gray-placeholder mt-0.5">
                    {TYPE_LABELS[type] ?? type}
                    {onVacation && (
                      <span className={`ml-1.5 font-semibold ${leaveColor}`}>· {leaveLabel}</span>
                    )}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* ── Scrollable timeline area ─────────────────────────────────────── */}
        <div className="flex-1 overflow-x-auto">
          <div style={{ width: TIMELINE_TOTAL_PX }} className="relative">
            {/* Time header */}
            <div className="h-10 border-b border-palette-gray-subtle relative select-none">
              {TIME_MARKERS.map(({ label, left, isHour }) => (
                <div
                  key={left}
                  className="absolute top-0 h-full flex items-center"
                  style={{ left }}
                >
                  <span
                    className={`pl-1 whitespace-nowrap ${
                      isHour
                        ? "text-xs font-semibold text-palette-gray-secondary"
                        : "text-[10px] text-palette-gray-placeholder"
                    }`}
                  >
                    {label}
                  </span>
                </div>
              ))}
            </div>

            {/* Staff timeline rows */}
            {staffForDay.map(({ p, slot, onVacation, leaveType }) => {
              const type = normalizeTypeForUi(p?.practitioner_type);
              const hasBlock = !!slot;

              // Leave blocks use a colour and label specific to the leave category;
              // shift blocks use the practitioner's role colour.
              const LEAVE_BLOCK_CLS = {
                vacation:
                  "bg-palette-amber-muted text-palette-amber-heading border-palette-amber-border",
                sick: "bg-palette-red-muted text-palette-red-heading border-palette-red-border",
                personal:
                  "bg-palette-violet-muted text-palette-violet-heading border-palette-violet-border",
                unpaid:
                  "bg-palette-gray-subtle text-palette-gray-badge border-palette-gray-divider",
              };
              const LEAVE_LABELS = {
                vacation: "Vacation",
                sick: "Sick Leave",
                personal: "Personal Day",
                unpaid: "Unpaid Leave",
              };
              const blockCls = onVacation
                ? (LEAVE_BLOCK_CLS[leaveType] ?? LEAVE_BLOCK_CLS.vacation)
                : typeColor(type); // role colour for any of the 16 types (was DDS/HYG-only)

              return (
                <div
                  key={p.id}
                  className="h-16 relative border-b border-palette-gray-subtle last:border-b-0"
                >
                  {/* Vertical grid lines */}
                  {TIME_MARKERS.map(({ left, isHour }) => (
                    <div
                      key={left}
                      className={`absolute top-0 bottom-0 ${isHour ? "w-px bg-palette-gray-divider" : "w-px bg-palette-gray-subtle"}`}
                      style={{ left }}
                    />
                  ))}

                  {/* Shift / leave block */}
                  {hasBlock && (
                    <div
                      className={`absolute top-2 bottom-2 rounded-2xl border flex flex-col justify-center px-3 overflow-hidden shadow-sm ${blockCls}${
                        onVacation ? "" : shiftChipClass(onShiftClick)
                      }`}
                      style={{
                        left: tlLeft(slot.start) + 2,
                        width: Math.max(56, tlWidth(slot.start, slot.end) - 4),
                      }}
                      // Leave blocks are not shifts — editing those belongs to Time Off.
                      {...(onVacation ? {} : shiftChipProps(onShiftClick, p, dateStr, slot))}
                    >
                      <span className="text-xs font-semibold leading-snug truncate">
                        {onVacation
                          ? (LEAVE_LABELS[leaveType] ?? "Time Off")
                          : (TYPE_LABELS[type] ?? type)}
                      </span>
                      <span className="text-xs opacity-75 leading-snug truncate mt-0.5">
                        {slot.start} – {slot.end}
                      </span>
                    </div>
                  )}

                  {/* Break bands — hatched blocks punched over the shift block. Rendered
                      after it (and z-[2]) so they sit on top, and skipped on leave blocks
                      where breaks are meaningless. Positioned in the row's coordinate space,
                      the same one the block uses. */}
                  {hasBlock &&
                    !onVacation &&
                    slotBreaks(slot)
                      .map((br, i) => ({ br, i, band: tlBand(br?.start, br?.end) }))
                      .filter(({ band }) => band)
                      .map(({ br, i, band }) => (
                        <div
                          key={i}
                          title={`Break · ${br.start} – ${br.end}`}
                          className="absolute top-1/2 z-[2] flex -translate-y-1/2 items-center justify-center overflow-hidden rounded-lg ring-1 ring-black/10 transition-transform hover:scale-[1.03]"
                          style={{
                            left: band.left + 2,
                            width: band.width,
                            height: BREAK_BAND_HEIGHT_PX,
                            background: BREAK_BAND_HATCH,
                          }}
                        >
                          <span className="flex items-center gap-1 px-1 text-[10px] font-medium text-[oklch(0.42_0.02_260)]">
                            {/* A 15-minute break is only 30px wide — the label would clip
                                to a sliver, so narrow bands show the hatch alone. */}
                            {band.width >= BREAK_LABEL_MIN_PX && "Break"}
                          </span>
                        </div>
                      ))}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Practice week view ─────────────────────────────────────────────────────────

function PracticeWeekView({
  roster,
  weekDays,
  overridesByPractitioner,
  schedulesByPractitioner,
  holidayMap,
  onShiftClick, // null when the viewer cannot manage shifts
}) {
  const staffWorkingCount = roster.filter((p) =>
    weekDays.some(({ dayKey: dk, dateStr: ds }) => {
      const s = getEffectiveSlot(p, ds, dk, overridesByPractitioner, schedulesByPractitioner);
      return !!(s?.start && s?.end);
    }),
  ).length;

  const totalShiftsCount = roster.reduce(
    (acc, p) =>
      acc +
      weekDays.filter(({ dayKey: dk, dateStr: ds }) => {
        const s = getEffectiveSlot(p, ds, dk, overridesByPractitioner, schedulesByPractitioner);
        return !!(s?.start && s?.end);
      }).length,
    0,
  );

  return (
    <div>
      {/* ── Stat cards ────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-4 gap-4 mb-4">
        <div className="bg-white rounded-2xl border border-palette-gray-subtle shadow-sm p-5">
          <p className="text-xs font-semibold text-palette-gray-placeholder uppercase tracking-widest mb-2">
            Staff Working
          </p>
          <p className="text-4xl font-bold text-palette-blue-badge">{staffWorkingCount}</p>
        </div>
        {[
          { label: "Total Shifts", value: totalShiftsCount },
          { label: "Days", value: weekDays.length },
          { label: "Staff", value: roster.length },
        ].map(({ label, value }) => (
          <div
            key={label}
            className="bg-white rounded-2xl border border-palette-gray-subtle shadow-sm p-5"
          >
            <p className="text-xs font-semibold text-palette-gray-placeholder uppercase tracking-widest mb-2">
              {label}
            </p>
            <p className="text-4xl font-bold text-palette-slate-text">{value}</p>
          </div>
        ))}
      </div>

      {/* ── Roster table — table-fixed fills 100% of the container so no horizontal
           scroll is needed; each day column gets an equal share of the available width. */}
      <div className="bg-white rounded-2xl border border-palette-gray-subtle shadow-sm overflow-hidden mb-4">
        <div>
          <table className="w-full table-fixed">
            <thead>
              <tr className="border-b border-palette-gray-subtle">
                {/* Employee column — fixed width so names don't squeeze */}
                <th className="text-left px-4 py-4 text-xs font-semibold text-palette-gray-placeholder uppercase tracking-wide w-44 border-r border-palette-gray-subtle">
                  Employee
                </th>
                {/* Day columns — auto-distributed across remaining width */}
                {weekDays.map(({ dayKey, dayShort, dayNum, dateStr: ds, isToday: isDayToday }) => (
                  <th
                    key={dayKey}
                    className={`px-2 py-4 text-center ${isDayToday ? "bg-palette-blue-soft/40" : ""}`}
                  >
                    <div className="flex flex-col items-center gap-1.5">
                      <span className="text-xs font-semibold text-palette-gray-placeholder uppercase tracking-wide">
                        {dayShort}
                      </span>
                      <span
                        className={`w-7 h-7 flex items-center justify-center rounded-full text-sm font-bold ${
                          isDayToday
                            ? "bg-palette-blue-badge text-white"
                            : "text-palette-slate-heading"
                        }`}
                      >
                        {dayNum}
                      </span>
                      {/* Holiday chip in column header so the whole column is visually marked */}
                      {holidayMap[ds] && <HolidayChip holiday={holidayMap[ds]} />}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {roster.length === 0 ? (
                <tr>
                  <td
                    colSpan={8}
                    className="py-12 text-center text-sm text-palette-gray-placeholder"
                  >
                    No staff found for this clinic.
                  </td>
                </tr>
              ) : (
                roster.map((p) => {
                  const name = safeText(p?.name) || "—";
                  const type = normalizeTypeForUi(p?.practitioner_type);
                  return (
                    <tr
                      key={p.id}
                      className="border-b border-palette-gray-subtle last:border-0 hover:bg-palette-gray-soft/40 transition-colors"
                    >
                      {/* Employee info cell — sticky */}
                      <td className="px-4 py-3.5 border-r border-palette-gray-subtle">
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${typeColor(type)}`}
                          >
                            {getInitials(name)}
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-palette-slate-text leading-snug">
                              {name}
                            </p>
                            <p className="text-xs text-palette-gray-placeholder leading-tight">
                              {TYPE_LABELS[type] ?? type}
                            </p>
                          </div>
                        </div>
                      </td>

                      {/* Day shift cells */}
                      {weekDays.map(({ dayKey, dateStr: ds, isToday: isDayToday }) => {
                        const slot = getEffectiveSlot(
                          p,
                          ds,
                          dayKey,
                          overridesByPractitioner,
                          schedulesByPractitioner,
                        );
                        const hasSlot = !!(slot?.start && slot?.end);
                        return (
                          <td
                            key={dayKey}
                            className={`px-2 py-3.5 text-center ${isDayToday ? "bg-palette-blue-soft/20" : ""}`}
                          >
                            {hasSlot ? (
                              <div
                                {...shiftChipProps(onShiftClick, p, ds, slot)}
                                className={`inline-flex items-center gap-1 px-1.5 py-1 rounded-lg text-xs font-semibold w-full justify-center ${typeColor(type)}${shiftChipClass(onShiftClick)}`}
                              >
                                <svg
                                  width="11"
                                  height="11"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  className="shrink-0"
                                >
                                  <circle cx="12" cy="12" r="10" />
                                  <polyline points="12 6 12 12 16 14" />
                                </svg>
                                <span>
                                  {slot.start} – {slot.end}
                                </span>
                              </div>
                            ) : (
                              <span
                                {...shiftChipProps(onShiftClick, p, ds, null)}
                                className={`text-palette-slate-ring text-base select-none${shiftChipClass(
                                  onShiftClick,
                                )}`}
                              >
                                —
                              </span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Roles legend ──────────────────────────────────────────────────── */}
      <RolesLegend />
    </div>
  );
}

// ── Employee view ──────────────────────────────────────────────────────────────

const ROLE_TYPES = [
  { label: "Dentist", color: "bg-palette-blue-indicator", type: "DDS" },
  { label: "Hygienist", color: "bg-palette-emerald-indicator", type: "HYG" },
  { label: "Practice Lead", color: "bg-palette-orange-indicator", type: "PL" }, // was amber — swapped with AM
  { label: "Area Manager", color: "bg-palette-amber-indicator", type: "AM" }, // was orange — swapped with PL
  { label: "Receptionist", color: "bg-palette-sky-indicator", type: "RCP" },
  { label: "Recall Coordinator", color: "bg-palette-purple-indicator", type: "RC" }, // was yellow — purple is far from PL orange
  { label: "Treatment Coordinator", color: "bg-palette-rose-indicator", type: "TC" }, // was teal — rose is far from HYG emerald
  { label: "Dental Assistant L1", color: "bg-palette-yellow-badge", type: "DAL1" }, // was violet — yellow is far from DDS blue
  { label: "Dental Assistant L2", color: "bg-palette-fuchsia-indicator", type: "DAL2" }, // was stone — fuchsia is far from FT stone
  { label: "Hygienist Restorative", color: "bg-palette-blue-indicator", type: "HYGR" }, // was green — blue is far from HYG emerald
  { label: "Nurse", color: "bg-palette-pink-indicator", type: "NUR" },
  { label: "RN", color: "bg-palette-violet-indicator", type: "RN" }, // was blue — violet frees blue for HYGR
  { label: "Floater", color: "bg-palette-stone-indicator", type: "FT" }, // was slate — stone is far from DAL2 fuchsia
  { label: "Sterilization", color: "bg-palette-teal-indicator", type: "STR" }, // was lime — teal frees lime for SPC
  { label: "Surgeon", color: "bg-palette-red-indicator", type: "SRG" },
  { label: "Specialist", color: "bg-palette-lime-indicator", type: "SPC" }, // was rose — lime is far from SRG red
];

// Extracted from 3 identical JSX blocks in PracticeMonthView, PracticeWeekView, and EmployeeView.
// Reads ROLE_TYPES from module scope — kept local because it uses dot-color classes (bg-*-400)
// for the legend dots, which differ from the background-pair classes in shared TYPE_COLORS.
function RolesLegend() {
  return (
    <div className="border-t border-palette-gray-subtle pt-4">
      <div className="bg-white rounded-2xl border border-palette-gray-subtle shadow-sm px-5 py-3.5">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-palette-gray-secondary">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
            Roles
          </div>
          <div className="h-4 w-px bg-palette-gray-divider" />
          {ROLE_TYPES.map(({ label, color }) => (
            <div key={label} className="flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-full ${color}`} />
              <span className="text-xs font-medium text-palette-slate-badge">{label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function EmployeeView({
  roster,
  dayKey,
  dateStr,
  vacationsByProfile,
  overridesByPractitioner,
  schedulesByPractitioner,
  clinicClosed,
  clinicClosures, // full closure rows — week/month cells need per-date checks, not one boolean
  view,
  weekDays,
  monthDays,
  holidayMap, // keyed by YYYY-MM-DD; values: { name, isPaidStat }
  onShiftClick, // (practitioner, dateStr, slot) => void; null when the user cannot edit
}) {
  const [selectedId, setSelectedId] = useState(roster[0]?.id ?? null);

  const selectedPractitioner = roster.find((p) => p.id === selectedId) ?? roster[0] ?? null;

  // Day-view status for the selected practitioner
  const dayStatus = useMemo(() => {
    if (!selectedPractitioner) return null;
    const slot = getEffectiveSlot(
      selectedPractitioner,
      dateStr,
      dayKey,
      overridesByPractitioner,
      schedulesByPractitioner,
      clinicClosures,
    );
    const hasSlot = !!(slot?.start && slot?.end);
    const pid = selectedPractitioner.id;
    const onVacation = isInRanges(dateStr, vacationsByProfile[pid]);
    return { slot: hasSlot ? slot : null, onVacation };
  }, [
    selectedPractitioner,
    dayKey,
    dateStr,
    vacationsByProfile,
    overridesByPractitioner,
    schedulesByPractitioner,
    clinicClosures,
  ]);

  // Week-view stats for the selected practitioner
  const weekStats = useMemo(() => {
    if (!selectedPractitioner || !weekDays) return { shifts: 0, hours: 0, daysOff: 0 };
    let shifts = 0;
    let hours = 0;
    for (const { dayKey: dk, dateStr: ds } of weekDays) {
      const slot = getEffectiveSlot(
        selectedPractitioner,
        ds,
        dk,
        overridesByPractitioner,
        schedulesByPractitioner,
        clinicClosures,
      );
      if (slot?.start && slot?.end) {
        shifts++;
        const diff = workingMinutes(slot); // net of breaks
        if (diff > 0) hours += diff / 60;
      }
    }
    return { shifts, hours, daysOff: 7 - shifts };
  }, [
    selectedPractitioner,
    weekDays,
    overridesByPractitioner,
    schedulesByPractitioner,
    clinicClosures,
  ]);

  // Month-view stats — skips vacation days so "Working Days" reflects actual availability
  const monthStats = useMemo(() => {
    if (!selectedPractitioner || !monthDays) return { workingDays: 0, hours: 0, daysOff: 0 };
    const pid = selectedPractitioner.id;
    let workingDays = 0;
    let hours = 0;
    let calDays = 0;
    for (const day of monthDays) {
      if (!day) continue;
      calDays++;
      const slot = getEffectiveSlot(
        selectedPractitioner,
        day.dateStr,
        day.dayKey,
        overridesByPractitioner,
        schedulesByPractitioner,
        clinicClosures,
      );
      if (slot?.start && slot?.end && !isInRanges(day.dateStr, vacationsByProfile[pid])) {
        workingDays++;
        const diff = workingMinutes(slot); // net of breaks
        if (diff > 0) hours += diff / 60;
      }
    }
    return { workingDays, hours, daysOff: calDays - workingDays };
  }, [
    selectedPractitioner,
    monthDays,
    vacationsByProfile,
    overridesByPractitioner,
    schedulesByPractitioner,
    clinicClosures,
  ]);

  const selName = safeText(selectedPractitioner?.name) || "—";
  const selType = normalizeTypeForUi(selectedPractitioner?.practitioner_type);
  const selPid = selectedPractitioner?.id ?? null;

  return (
    <div>
      {/* ── Statutory holiday banner (day view only) ────────────────────── */}
      {view === "day" && holidayMap[dateStr] && (
        <div className="mb-4 flex items-center gap-2.5 px-4 py-3 rounded-xl bg-palette-red-soft border border-palette-red-border">
          <span className="text-palette-red-label shrink-0">🏛</span>
          <span className="text-sm font-semibold text-palette-red-heading">
            {holidayMap[dateStr].name}
          </span>
          {holidayMap[dateStr].isPaidStat && (
            <span className="text-xs text-palette-red-label font-medium">
              · Paid statutory holiday
            </span>
          )}
        </div>
      )}

      {/* ── Staff bubble row ──────────────────────────────────────────────── */}
      <div className="flex flex-wrap gap-2 mb-6">
        {roster.length === 0 ? (
          <p className="text-sm text-palette-gray-placeholder py-2">
            No staff found for this clinic.
          </p>
        ) : (
          roster.map((p) => {
            const pName = safeText(p?.name) || "—";
            const isSelected = p.id === selectedPractitioner?.id;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => setSelectedId(p.id)}
                className={`flex items-center gap-2 px-3 py-2 rounded-full border whitespace-nowrap shrink-0 transition-all duration-200 ease-in-out ${
                  isSelected
                    ? "border-palette-blue-ring bg-palette-blue-soft shadow-sm"
                    : "border-palette-gray-divider bg-white hover:border-palette-gray-border hover:bg-palette-gray-soft"
                }`}
              >
                <span
                  className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${typeColor(normalizeTypeForUi(p?.practitioner_type))}`}
                >
                  {getInitials(pName)}
                </span>
                <span
                  className={`text-sm font-medium ${isSelected ? "text-palette-blue-heading" : "text-palette-slate-heading"}`}
                >
                  {pName}
                </span>
              </button>
            );
          })
        )}
      </div>

      {/* ── Employee detail card ──────────────────────────────────────────── */}
      {selectedPractitioner && (
        <div className="bg-white rounded-2xl border border-palette-gray-subtle shadow-sm px-6 py-5 mb-4">
          <div className="flex items-center justify-between gap-4">
            {/* Left: avatar + name + role badge */}
            <div className="flex items-center gap-4">
              <div
                className={`w-14 h-14 rounded-full flex items-center justify-center text-lg font-bold shrink-0 ${typeColor(selType)}`}
              >
                {getInitials(selName)}
              </div>
              <div>
                <p className="text-base font-bold text-palette-slate-text leading-snug">
                  {selName}
                </p>
                <Badge color={pickBadgeColor(selType)} className="mt-1.5 font-semibold">
                  {TYPE_LABELS[selType] ?? selType}
                </Badge>
              </div>
            </div>

            {/* Day view: today's shift on the right */}
            {view === "day" && dayStatus && (
              <div className="text-right shrink-0">
                <p className="text-xs font-semibold text-palette-gray-placeholder uppercase tracking-widest mb-1">
                  Today
                </p>
                {clinicClosed ? (
                  <p className="text-xl font-bold text-palette-slate-placeholder">Clinic Closed</p>
                ) : dayStatus.onVacation ? (
                  <p className="text-xl font-bold text-palette-amber-label">Time Off</p>
                ) : dayStatus.slot ? (
                  <p
                    {...shiftChipProps(onShiftClick, selectedPractitioner, dateStr, dayStatus.slot)}
                    className={`text-2xl font-bold text-palette-blue-badge tabular-nums${shiftChipClass(onShiftClick)}`}
                  >
                    {dayStatus.slot.start} – {dayStatus.slot.end}
                  </p>
                ) : (
                  <p
                    {...shiftChipProps(
                      shouldOfferShiftEdit(onShiftClick, { clinicClosed }),
                      selectedPractitioner,
                      dateStr,
                      null,
                    )}
                    className={`text-xl font-bold text-palette-gray-placeholder${shiftChipClass(
                      shouldOfferShiftEdit(onShiftClick, { clinicClosed }),
                    )}`}
                  >
                    Not Scheduled
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Week view: stat cards + 7-day grid ───────────────────────────── */}
      {view === "week" && selectedPractitioner && weekDays && (
        <>
          {/* Stat cards */}
          <div className="grid grid-cols-3 gap-4 mb-4">
            {[
              {
                label: "Shifts This Week",
                value: weekStats.shifts,
                color: "text-palette-blue-badge",
              },
              {
                label: "Hours This Week",
                value: Math.round(weekStats.hours),
                color: "text-palette-blue-badge",
              },
              { label: "Days Off", value: weekStats.daysOff, color: "text-palette-slate-text" },
            ].map(({ label, value, color }) => (
              <div
                key={label}
                className="bg-white rounded-2xl border border-palette-gray-subtle shadow-sm p-5"
              >
                <p className="text-xs font-semibold text-palette-gray-placeholder uppercase tracking-widest mb-2">
                  {label}
                </p>
                <p className={`text-4xl font-bold ${color}`}>{value}</p>
              </div>
            ))}
          </div>

          {/* 7-day grid */}
          <div className="grid grid-cols-7 gap-3 mb-4">
            {weekDays.map(
              ({ dayKey: dk, dayShort, dayNum, dateStr: dStr, isToday: isDayToday }) => {
                const slot = getEffectiveSlot(
                  selectedPractitioner,
                  dStr,
                  dk,
                  overridesByPractitioner,
                  schedulesByPractitioner,
                  clinicClosures,
                );
                const hasSlot = !!(slot?.start && slot?.end);
                const leaveType = getLeaveForDate(dStr, vacationsByProfile[selPid]);
                const dayClosed = (clinicClosures ?? []).some((cv) =>
                  isInRanges(dStr, cv.date_ranges),
                );
                return (
                  <div
                    key={dk}
                    className={`bg-white rounded-xl p-3 flex flex-col gap-2 min-h-[100px] shadow-sm ${
                      isDayToday
                        ? "border-2 border-palette-blue-indicator"
                        : "border border-palette-gray-subtle"
                    }`}
                  >
                    <div>
                      <p className="text-xs font-semibold text-palette-gray-placeholder uppercase tracking-wide">
                        {dayShort}
                      </p>
                      <p className="text-2xl font-bold text-palette-slate-text leading-tight">
                        {dayNum}
                      </p>
                    </div>
                    {/* Holiday chip — displayed below the date for quick visibility */}
                    <HolidayChip holiday={holidayMap[dStr]} />
                    {leaveType ? (
                      <LeaveIndicator leaveType={leaveType} />
                    ) : hasSlot ? (
                      <span
                        {...shiftChipProps(onShiftClick, selectedPractitioner, dStr, slot)}
                        className={`inline-flex items-center px-2 py-1 rounded-lg text-xs font-semibold leading-snug ${typeColor(selType)}${shiftChipClass(onShiftClick)}`}
                      >
                        {slot.start} – {slot.end}
                      </span>
                    ) : (
                      <span
                        {...shiftChipProps(
                          shouldOfferShiftEdit(onShiftClick, { clinicClosed: dayClosed }),
                          selectedPractitioner,
                          dStr,
                          null,
                        )}
                        className={`text-sm text-palette-gray-placeholder${shiftChipClass(
                          shouldOfferShiftEdit(onShiftClick, { clinicClosed: dayClosed }),
                        )}`}
                      >
                        Off
                      </span>
                    )}
                  </div>
                );
              },
            )}
          </div>
        </>
      )}

      {/* ── Month view: stat cards + full calendar grid ──────────────────── */}
      {view === "month" && selectedPractitioner && monthDays && (
        <>
          {/* Stat cards */}
          <div className="grid grid-cols-3 gap-4 mb-4">
            {[
              {
                label: "Working Days",
                value: monthStats.workingDays,
                color: "text-palette-blue-badge",
              },
              {
                label: "Total Hours",
                value: Math.round(monthStats.hours),
                color: "text-palette-slate-text",
              },
              { label: "Days Off", value: monthStats.daysOff, color: "text-palette-slate-text" },
            ].map(({ label, value, color }) => (
              <div
                key={label}
                className="bg-white rounded-2xl border border-palette-gray-subtle shadow-sm p-5"
              >
                <p className="text-xs font-semibold text-palette-gray-placeholder uppercase tracking-widest mb-2">
                  {label}
                </p>
                <p className={`text-4xl font-bold ${color}`}>{value}</p>
              </div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="bg-white rounded-2xl border border-palette-gray-subtle shadow-sm overflow-hidden mb-4">
            {/* Column headers */}
            <div className="grid grid-cols-7 border-b border-palette-gray-subtle">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                <div
                  key={d}
                  className="py-2.5 text-center text-xs font-semibold text-palette-gray-placeholder uppercase tracking-wide"
                >
                  {d}
                </div>
              ))}
            </div>

            {/* Day cells — null entries are leading/trailing padding */}
            <div className="grid grid-cols-7">
              {monthDays.map((day, i) => {
                if (!day) {
                  return (
                    <div
                      key={`pad-${i}`}
                      className="border-b border-r border-palette-gray-subtle bg-palette-gray-soft/40 min-h-[100px]"
                    />
                  );
                }
                const slot = getEffectiveSlot(
                  selectedPractitioner,
                  day.dateStr,
                  day.dayKey,
                  overridesByPractitioner,
                  schedulesByPractitioner,
                  clinicClosures,
                );
                const hasSlot = !!(slot?.start && slot?.end);
                const leaveType = getLeaveForDate(day.dateStr, vacationsByProfile[selPid]);
                // An "Off" cell is only editable when the practice is actually open —
                // adding a shift onto a clinic closure from a calendar cell is never intended.
                const dayClosed = (clinicClosures ?? []).some((cv) =>
                  isInRanges(day.dateStr, cv.date_ranges),
                );
                return (
                  <div
                    key={day.dateStr}
                    className="border-b border-r border-palette-gray-subtle p-3 min-h-[100px] flex flex-col gap-2"
                  >
                    {/* Date number */}
                    <span
                      className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-sm font-bold self-start ${
                        day.isToday
                          ? "bg-palette-blue-badge text-white"
                          : "text-palette-slate-heading"
                      }`}
                    >
                      {day.dayNum}
                    </span>

                    {/* Holiday chip for this day */}
                    <HolidayChip holiday={holidayMap[day.dateStr]} />

                    {/* Leave indicator, shift pill, or Off */}
                    {leaveType ? (
                      <LeaveIndicator leaveType={leaveType} />
                    ) : hasSlot ? (
                      <div
                        {...shiftChipProps(onShiftClick, selectedPractitioner, day.dateStr, slot)}
                        className={`flex items-center gap-1 bg-palette-sky-soft text-palette-sky-badge rounded-lg px-2 py-1 text-xs font-semibold${shiftChipClass(onShiftClick)}`}
                      >
                        <svg
                          width="11"
                          height="11"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="shrink-0"
                        >
                          <circle cx="12" cy="12" r="10" />
                          <polyline points="12 6 12 12 16 14" />
                        </svg>
                        <span className="leading-tight">
                          {slot.start} – {slot.end}
                        </span>
                      </div>
                    ) : (
                      <span
                        {...shiftChipProps(
                          shouldOfferShiftEdit(onShiftClick, { clinicClosed: dayClosed }),
                          selectedPractitioner,
                          day.dateStr,
                          null,
                        )}
                        className={`text-sm text-palette-gray-placeholder${shiftChipClass(
                          shouldOfferShiftEdit(onShiftClick, { clinicClosed: dayClosed }),
                        )}`}
                      >
                        Off
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* ── Roles legend ──────────────────────────────────────────────────── */}
      <RolesLegend />
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

// ── Clinic selector dropdown ───────────────────────────────────────────────────

function ClinicSelector({ clinics, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);

  const selected = clinics.find((c) => c.clinic === value);
  const displayName = selected?.name?.trim() ? selected.name : value;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return clinics;
    return clinics.filter(
      (c) => (c.name || "").toLowerCase().includes(q) || c.clinic.toLowerCase().includes(q),
    );
  }, [clinics, search]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handle(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setSearch("");
      }
    }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      {/* ── Trigger button ──────────────────────────────────────────────── */}
      <button
        type="button"
        onClick={() => {
          setOpen((o) => !o);
          setSearch("");
        }}
        className="flex items-center gap-2 px-3 py-2 bg-white border border-palette-gray-divider rounded-xl text-sm font-semibold text-palette-slate-heading hover:border-palette-blue-ring hover:bg-palette-blue-soft/30 transition-all duration-150 min-w-[260px] max-w-[400px]"
      >
        {/* Building icon */}
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-palette-blue-label shrink-0"
        >
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
          <polyline points="9 22 9 12 15 12 15 22" />
        </svg>
        <span className="flex-1 text-left truncate">{displayName}</span>
        {/* Chevron rotates when open */}
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          className={`text-palette-gray-placeholder shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {/* ── Dropdown panel ───────────────────────────────────────────────── */}
      {open && (
        <div className="absolute left-0 top-full mt-2 z-50 bg-white rounded-2xl border border-palette-gray-subtle shadow-xl overflow-hidden w-[380px]">
          {/* Search input */}
          <div className="px-3 py-2.5 border-b border-palette-gray-subtle">
            <div className="relative">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 text-palette-gray-placeholder pointer-events-none"
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search clinics..."
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
                className="w-full pl-8 pr-3 py-2 text-sm bg-palette-gray-soft rounded-lg border border-palette-gray-divider outline-none focus:ring-2 focus:ring-palette-blue-ring focus:border-palette-blue-ring text-palette-slate-heading placeholder-palette-gray-placeholder"
              />
            </div>
          </div>

          {/* Clinic list */}
          <div className="overflow-y-auto max-h-64 py-1">
            {filtered.length === 0 ? (
              <p className="px-4 py-4 text-sm text-palette-gray-placeholder text-center">
                No clinics found
              </p>
            ) : (
              filtered.map((c) => {
                const isActive = c.clinic === value;
                const name = c.name?.trim() || c.clinic;
                return (
                  <button
                    key={c.clinic}
                    type="button"
                    onClick={() => {
                      onChange(c.clinic);
                      setOpen(false);
                      setSearch("");
                    }}
                    className={`w-full text-left flex items-center justify-between px-4 py-3 transition-colors ${
                      isActive ? "bg-palette-blue-soft" : "hover:bg-palette-gray-subtle"
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <p
                        className={`text-sm font-semibold truncate ${isActive ? "text-palette-blue-heading" : "text-palette-slate-text"}`}
                      >
                        {name}
                      </p>
                      <p className="text-xs text-palette-gray-placeholder mt-0.5">{c.clinic}</p>
                    </div>
                    {isActive && (
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="shrink-0 ml-3 text-palette-indigo-badge"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function SchedulePage() {
  const { clinicId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Fetch clinic list so the user can switch clinics from the action bar.
  // Only roles that can list all clinics (Organizer/Admin/Observer/Payroll) hit
  // the API. Area Managers and below fall back to user.clinic assignments.
  const canListAll = canAccessAllClinics(user);
  const { data: clinicsData } = useClinics({ limit: 10000, offset: 0 }, { enabled: canListAll });

  // Fetch this clinic's detail so we can read its province for holiday filtering.
  const { data: clinicDetail } = useClinicDetail(clinicId);

  const allClinics = useMemo(() => {
    if (canListAll) {
      return (clinicsData?.results || [])
        .slice()
        .sort((a, b) => (a.name || a.clinic || "").localeCompare(b.name || b.clinic || ""));
    }
    // Area Manager / Practitioner Lead: scope to assigned clinic(s)
    const assigned = Array.isArray(user?.clinic) ? user.clinic : user?.clinic ? [user.clinic] : [];
    if (assigned.length) return assigned.map((c) => ({ clinic: c, name: "" }));
    return clinicId ? [{ clinic: clinicId, name: "" }] : [];
  }, [clinicsData, canListAll, user, clinicId]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const [currentDate, setCurrentDate] = useState(today);
  const [activeTab, setActiveTab] = useState("practice");
  const [view, setView] = useState("day");

  // ── Data fetching ────────────────────────────────────────────────────────────

  const { data: practitionersData, isLoading: practLoading } = usePractitioners({
    clinic: clinicId,
  });

  const { data: clinicVacData } = useClinicVacations({ clinic: clinicId });
  const clinicClosures = useMemo(() => clinicVacData?.results ?? [], [clinicVacData]);

  const { data: practVacData } = usePractitionerVacations({ clinic: clinicId });
  const practVacations = useMemo(() => practVacData?.results ?? practVacData ?? [], [practVacData]);

  // Every weekly-schedule row for the clinic (base + date-bounded) in one request; the
  // views resolve which row applies per date, so paging by date isn't needed.
  const { data: weeklyScheduleData } = usePractitionerWeeklySchedules({ clinic: clinicId });
  const schedulesByPractitioner = useMemo(
    () => groupSchedulesByPractitioner(weeklyScheduleData),
    [weeklyScheduleData],
  );

  const { data: practOverrideData } = usePractitionerScheduleOverrides({ clinic: clinicId });
  const overridesByPractitioner = useMemo(() => {
    const map = {};
    for (const ov of practOverrideData?.results ?? []) {
      const pid = ov.practitioner;
      if (pid != null) map[pid] = (map[pid] || []).concat(ov);
    }
    return map;
  }, [practOverrideData]);

  // ── Single-day shift editor ──────────────────────────────────────────────────
  // One state object + one modal instance serves all six calendar views. Each view's
  // shift chip just reports which practitioner/date/slot was clicked.
  //
  // Gate mirrors the backend's has_role([1, 2, 3]) on
  // PractitionerScheduleOverrideViewSet. Practitioner Lead (4) is excluded there, so it
  // must be excluded here too or the buttons 403 on submit.
  const canManageShifts = isManagerOrAbove(user);

  const [editingShift, setEditingShift] = useState(null);
  const [shiftEditorError, setShiftEditorError] = useState("");
  // Save and Delete share one mutation pair, so track which action is in flight —
  // otherwise pressing Delete also makes the Save button read "Saving...".
  const [shiftAction, setShiftAction] = useState(null); // "save" | "delete" | null
  const createOverride = useCreatePractitionerScheduleOverride();
  const updateOverride = useUpdatePractitionerScheduleOverride();

  const openShiftEditor = useCallback(
    (practitioner, dateStr, slot) => {
      if (!practitioner || !dateStr) return;
      setShiftEditorError("");
      setEditingShift({
        practitioner,
        dateStr,
        slot,
        overrideRow: findOverrideForDate(overridesByPractitioner, practitioner.id, dateStr),
      });
    },
    [overridesByPractitioner],
  );

  // Writes a one-day override for the clicked date. `isOpen: false` is how a single
  // occurrence is removed — the recurring weekly row is never touched either way.
  // Upserts when the date already has an override so rows can't silently accumulate.
  const saveShiftOverride = useCallback(
    async ({ isOpen, start, end }) => {
      if (!editingShift) return;
      const { practitioner, dateStr, overrideRow } = editingShift;
      const payload = {
        practitioner: Number(practitioner.id), // Practitioner PK (clinic-assignment)
        date_ranges: [{ start_date: dateStr, end_date: dateStr }],
        is_open: isOpen,
        start_time: isOpen ? start : "",
        end_time: isOpen ? end : "",
      };
      setShiftEditorError("");
      setShiftAction(isOpen ? "save" : "delete");
      try {
        if (overrideRow?.id) {
          await updateOverride.mutateAsync({ id: overrideRow.id, data: payload });
        } else {
          await createOverride.mutateAsync(payload);
        }
        showSuccess(isOpen ? "Shift updated for this date." : "Shift removed for this date.");
        setEditingShift(null);
      } catch {
        setShiftEditorError(
          isOpen ? "Failed to update this shift." : "Failed to remove this shift.",
        );
      } finally {
        setShiftAction(null);
      }
    },
    [editingShift, createOverride, updateOverride],
  );

  // Vacations are now keyed by Practitioner PK (clinic-assignment) — each vacation
  // belongs to one specific practitioner-clinic pair, not just a person.
  const vacationsByProfile = useMemo(() => {
    const map = {};
    for (const v of practVacations) {
      const pid = v.practitioner; // Practitioner PK from the API
      if (pid != null) {
        const ranges = (v.date_ranges ?? [])
          // Only APPROVED leave belongs on the calendar. status is embedded per-range,
          // like leave_type — a denied or pending request must not render as time off.
          // `status == null` still counts as approved for legacy pre-migration-0031
          // rows, matching the backend resolver and FinancialReport's
          // activeVacationRange. Filtering here fixes every consumer at once:
          // isInRanges, getLeaveForDate and the month / week / day views all read
          // through this map.
          .filter((r) => r.status === "approved" || r.status == null)
          .map((r) => ({
            start_date: r.start_date,
            end_date: r.end_date,
            // leave_type is now embedded per-range (redesigned model) — read from r, not v
            leave_type: r.leave_type || "vacation",
          }));
        map[pid] = (map[pid] || []).concat(ranges);
      }
    }
    return map;
  }, [practVacations]);

  const roster = useMemo(() => {
    return getRoster(practitionersData)
      .filter((p) => safeText(p?.name) && safeText(p?.status).toLowerCase() !== "inactive")
      .slice()
      .sort((a, b) => {
        const tA = TYPE_ORDER[normalizeTypeForUi(a?.practitioner_type)] ?? 99;
        const tB = TYPE_ORDER[normalizeTypeForUi(b?.practitioner_type)] ?? 99;
        return tA !== tB ? tA - tB : safeText(a?.name).localeCompare(safeText(b?.name));
      });
  }, [practitionersData]);

  // ── Holiday map ──────────────────────────────────────────────────────────────

  // Keyed only on year + province so day/week navigation doesn't recompute unnecessarily.
  // Three-year window covers Dec→Jan and Jan→Dec boundary navigation.
  const currentYear = currentDate.getFullYear();
  const holidayMap = useMemo(
    () =>
      getHolidayMap(
        [currentYear - 1, currentYear, currentYear + 1],
        clinicDetail?.province || null,
      ),
    [currentYear, clinicDetail],
  );

  // ── Day calculations ─────────────────────────────────────────────────────────

  const dateStr = toDateStr(currentDate);
  const dayKey = DAY_KEYS[currentDate.getDay()];

  const clinicClosed = useMemo(
    () => clinicClosures.some((cv) => isInRanges(dateStr, cv.date_ranges)),
    [clinicClosures, dateStr],
  );

  const staffForDay = useMemo(() => {
    return roster
      .map((p) => {
        const slot = getEffectiveSlot(
          p,
          dateStr,
          dayKey,
          overridesByPractitioner,
          schedulesByPractitioner,
        );
        const hasSlot = !!(slot?.start && slot?.end);
        const pid = p.id;
        // leaveType is the specific leave category (vacation/sick/personal/unpaid)
        // or null when the practitioner is not on leave — used by the timeline
        // to show a labelled leave block instead of a generic "Time Off" label.
        const leaveType = getLeaveForDate(dateStr, vacationsByProfile[pid]);
        const onVacation = !!leaveType;
        return {
          p,
          slot: hasSlot ? slot : null,
          onVacation,
          leaveType,
          working: hasSlot && !onVacation,
        };
      })
      .filter(({ slot, onVacation }) => slot || onVacation);
  }, [
    roster,
    dayKey,
    dateStr,
    vacationsByProfile,
    overridesByPractitioner,
    schedulesByPractitioner,
  ]);

  const workingCount = staffForDay.filter((x) => x.working).length;
  const offCount = staffForDay.filter((x) => x.onVacation).length;

  const totalHours = useMemo(
    () =>
      staffForDay.reduce((acc, { slot, working }) => {
        if (!working || !slot) return acc;
        const diff = workingMinutes(slot); // net of breaks
        return diff > 0 ? acc + diff / 60 : acc;
      }, 0),
    [staffForDay],
  );

  const uniqueRoles = useMemo(() => {
    return new Set(
      staffForDay.filter((x) => x.working).map((x) => normalizeTypeForUi(x.p?.practitioner_type)),
    ).size;
  }, [staffForDay]);

  // ── Week / month data ────────────────────────────────────────────────────────

  const todayStr = toDateStr(today);
  const weekDays = useMemo(() => buildWeekDays(currentDate, todayStr), [currentDate, todayStr]);
  const monthDays = useMemo(() => buildMonthDays(currentDate, todayStr), [currentDate, todayStr]);
  const practiceMonthGrid = useMemo(
    () => buildPracticeMonthGrid(currentDate, todayStr),
    [currentDate, todayStr],
  );

  // ── Date navigation ──────────────────────────────────────────────────────────

  const isMonthView = view === "month"; // both Practice and Employee month views
  const isWeekView = view === "week"; // both Practice and Employee week views

  function prevDay() {
    setCurrentDate((d) => {
      const n = new Date(d);
      if (isMonthView) {
        n.setMonth(n.getMonth() - 1, 1);
      } else {
        n.setDate(n.getDate() - (isWeekView ? 7 : 1));
      }
      return n;
    });
  }

  function nextDay() {
    setCurrentDate((d) => {
      const n = new Date(d);
      if (isMonthView) {
        n.setMonth(n.getMonth() + 1, 1);
      } else {
        n.setDate(n.getDate() + (isWeekView ? 7 : 1));
      }
      return n;
    });
  }

  const isToday = dateStr === todayStr;

  const dateLabel = isMonthView
    ? getMonthLabel(currentDate)
    : isWeekView
      ? formatWeekLabel(currentDate)
      : formatFullDate(currentDate);

  const dentistCount = roster.filter(
    (p) => normalizeTypeForUi(p.practitioner_type) === "DDS",
  ).length;

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-palette-gray-soft">
      {/* ── Action bar ──────────────────────────────────────────────────────── */}
      <div className="bg-white border-b border-palette-gray-divider px-6 py-3">
        <div className="flex items-center justify-between">
          {/* Left: back + clinic info */}
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/report/${clinicId}`)}
              className="text-palette-gray-placeholder hover:text-palette-slate-badge"
            >
              ← Back
            </Button>

            <div className="h-4 w-px bg-palette-gray-divider" />

            <ClinicSelector
              clinics={allClinics}
              value={clinicId}
              onChange={(id) => navigate(`/report/${id}/schedule`)}
            />

            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-palette-gray-secondary bg-palette-gray-subtle px-2.5 py-1 rounded-full">
                {roster.length} staff
              </span>
              <span className="text-xs font-medium text-palette-gray-secondary bg-palette-gray-subtle px-2.5 py-1 rounded-full">
                {dentistCount} dentist{dentistCount !== 1 ? "s" : ""}
              </span>
            </div>
          </div>

          {/* Right: action buttons */}
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/report/${clinicId}/schedule/vacation-request`)}
              className="gap-2 border border-palette-gray-divider rounded-xl hover:bg-palette-gray-soft transition-all duration-150 ease-in-out"
            >
              ✈️ Request vacation
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/report/${clinicId}/schedule/approvals`)}
              className="border border-palette-gray-divider rounded-xl hover:bg-palette-gray-soft transition-all duration-150 ease-in-out"
            >
              Approvals
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => navigate(`/report/${clinicId}/schedule/new-shift`)}
              className="gap-1.5 rounded-xl hover:shadow-md transition-all duration-150 ease-in-out"
            >
              + New shift
            </Button>
          </div>
        </div>
      </div>

      {/* ── Tab row + view switcher ──────────────────────────────────────────── */}
      <div className="bg-white border-b border-palette-gray-divider px-6">
        <div className="flex items-center justify-between">
          {/* Practice / Employee tabs */}
          <div className="flex">
            {[
              { key: "practice", label: "Practice" },
              { key: "employee", label: "Employee" },
            ].map(({ key, label }) => (
              <button
                key={key}
                type="button"
                onClick={() => setActiveTab(key)}
                className={`px-4 py-3 text-sm font-semibold border-b-2 transition-all duration-200 ease-in-out ${
                  activeTab === key
                    ? "border-palette-blue-badge text-palette-blue-heading"
                    : "border-transparent text-palette-gray-secondary hover:text-palette-slate-heading"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Day / Week / Month switcher */}
          <div className="flex items-center gap-1 bg-palette-gray-subtle p-1 rounded-xl">
            {["day", "week", "month"].map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setView(v)}
                className={`px-4 py-1.5 rounded-lg text-sm font-medium capitalize transition-all duration-200 ease-in-out ${
                  view === v
                    ? "bg-white text-palette-slate-text shadow-sm"
                    : "text-palette-gray-secondary hover:text-palette-slate-heading"
                }`}
              >
                {v.charAt(0).toUpperCase() + v.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Main content ────────────────────────────────────────────────────── */}
      <div className="px-6 py-6">
        {/* Date navigation */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={prevDay}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-palette-gray-divider text-palette-gray-badge text-xl font-medium transition-all duration-200 ease-in-out"
              >
                ‹
              </button>
              <button
                type="button"
                onClick={nextDay}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-palette-gray-divider text-palette-gray-badge text-xl font-medium transition-all duration-200 ease-in-out"
              >
                ›
              </button>
            </div>
            <h2 className="text-xl font-bold text-palette-slate-text">{dateLabel}</h2>
          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => setCurrentDate(today)}
            disabled={isToday}
            className="border border-palette-gray-divider rounded-lg hover:bg-palette-gray-soft transition-all duration-150 ease-in-out"
          >
            Today
          </Button>
        </div>

        {/* Statutory holiday banner — Practice day view */}
        {activeTab === "practice" && view === "day" && holidayMap[dateStr] && (
          <div className="mb-4 flex items-center gap-2.5 px-4 py-3 rounded-xl bg-palette-red-soft border border-palette-red-border">
            <span className="text-palette-red-label shrink-0">🏛</span>
            <span className="text-sm font-semibold text-palette-red-heading">
              {holidayMap[dateStr].name}
            </span>
            {holidayMap[dateStr].isPaidStat && (
              <span className="text-xs text-palette-red-label font-medium">
                · Paid statutory holiday
              </span>
            )}
          </div>
        )}

        {/* Stats cards — Practice Day view only */}
        {activeTab === "practice" && view === "day" && (
          <div className="grid grid-cols-4 gap-4 mb-6">
            <StatCard label="Working" value={clinicClosed ? 0 : workingCount} />
            <StatCard label="Total Hours" value={clinicClosed ? 0 : Math.round(totalHours)} />
            <StatCard label="Roles" value={clinicClosed ? 0 : uniqueRoles} />
            <StatCard label="Off Today" value={offCount} />
          </div>
        )}

        {/* ── Employee view ──────────────────────────────────────────────────── */}
        {activeTab === "employee" && (
          <EmployeeView
            roster={roster}
            dayKey={dayKey}
            dateStr={dateStr}
            vacationsByProfile={vacationsByProfile}
            overridesByPractitioner={overridesByPractitioner}
            schedulesByPractitioner={schedulesByPractitioner}
            clinicClosed={clinicClosed}
            clinicClosures={clinicClosures}
            view={view}
            weekDays={weekDays}
            monthDays={monthDays}
            holidayMap={holidayMap}
            onShiftClick={canManageShifts ? openShiftEditor : null}
          />
        )}

        {/* ── Day view (Practice tab) ────────────────────────────────────────── */}
        {activeTab === "practice" && view === "day" && (
          <PracticeDayTimeline
            staffForDay={staffForDay}
            clinicClosed={clinicClosed}
            practLoading={practLoading}
            dateStr={dateStr}
            onShiftClick={canManageShifts ? openShiftEditor : null}
          />
        )}

        {/* ── Practice week view ───────────────────────────────────────────── */}
        {activeTab === "practice" && view === "week" && (
          <PracticeWeekView
            roster={roster}
            weekDays={weekDays}
            overridesByPractitioner={overridesByPractitioner}
            schedulesByPractitioner={schedulesByPractitioner}
            holidayMap={holidayMap}
            onShiftClick={canManageShifts ? openShiftEditor : null}
          />
        )}

        {/* ── Practice month view ──────────────────────────────────────────── */}
        {activeTab === "practice" && view === "month" && (
          <PracticeMonthView
            roster={roster}
            practiceMonthGrid={practiceMonthGrid}
            clinicClosures={clinicClosures}
            vacationsByProfile={vacationsByProfile}
            overridesByPractitioner={overridesByPractitioner}
            schedulesByPractitioner={schedulesByPractitioner}
            holidayMap={holidayMap}
            onShiftClick={canManageShifts ? openShiftEditor : null}
          />
        )}
      </div>

      {/* Single editor instance for all six views — driven purely by `editingShift`.
          Placed here so a modal opened from any view is unaffected by that view's layout. */}
      <EditShiftModal
        isOpen={!!editingShift}
        onClose={() => setEditingShift(null)}
        practitionerName={safeText(editingShift?.practitioner?.name) || ""}
        dateStr={editingShift?.dateStr || ""}
        slot={editingShift?.slot || null}
        overrideRow={editingShift?.overrideRow || null}
        onSave={({ start, end }) => saveShiftOverride({ isOpen: true, start, end })}
        onDelete={() => saveShiftOverride({ isOpen: false })}
        isSaving={shiftAction === "save"}
        isDeleting={shiftAction === "delete"}
        error={shiftEditorError}
      />
    </div>
  );
}
